import React, { useState, useEffect } from 'react'
import { ArrowLeft, Save, Users, Calendar, CheckCircle, XCircle, MessageSquare, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Session, Registration, Presence } from '../types/database'
import { useAuth } from '../hooks/useAuth'

interface PresenceManagementProps {
  session: Session
  onBack: () => void
}

interface RegistrationWithPresence extends Registration {
  presence?: Presence
}

export function PresenceManagement({ session, onBack }: PresenceManagementProps) {
  const { userProfile } = useAuth()
  const [registrations, setRegistrations] = useState<RegistrationWithPresence[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  useEffect(() => {
    fetchRegistrations()
  }, [session.id])

  const fetchRegistrations = async () => {
    try {
      const { data: registrationsData, error: regError } = await supabase
        .from('registrations')
        .select(`
          *,
          kid:kids(*),
          user:users(*)
        `)
        .eq('activity_id', session.id)
        .eq('cancellation_status', 'none')

      if (regError) throw regError

      // Fetch existing presences
      const { data: presencesData, error: presError } = await supabase
        .from('presences')
        .select('*')
        .in('registration_id', registrationsData?.map(r => r.id) || [])

      if (presError) throw presError

      // Merge registrations with presences
      const registrationsWithPresences = registrationsData?.map(reg => ({
        ...reg,
        presence: presencesData?.find(p => p.registration_id === reg.id)
      })) || []

      setRegistrations(registrationsWithPresences)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const updatePresence = (registrationId: string, day: string, present: boolean) => {
    setHasUnsavedChanges(true)
    setRegistrations(prev => prev.map(reg => {
      if (reg.id === registrationId) {
        const currentPresence = reg.presence || {
          id: '',
          registration_id: registrationId,
          monday_present: true,
          tuesday_present: true,
          wednesday_present: true,
          thursday_present: true,
          friday_present: true,
          notes: '',
          recorded_by_user_id: userProfile?.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        return {
          ...reg,
          presence: {
            ...currentPresence,
            [`${day}_present`]: present
          }
        }
      }
      return reg
    }))
  }

  const updateNotes = (registrationId: string, notes: string) => {
    setHasUnsavedChanges(true)
    setRegistrations(prev => prev.map(reg => {
      if (reg.id === registrationId) {
        const currentPresence = reg.presence || {
          id: '',
          registration_id: registrationId,
          monday_present: true,
          tuesday_present: true,
          wednesday_present: true,
          thursday_present: true,
          friday_present: true,
          notes: '',
          recorded_by_user_id: userProfile?.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        return {
          ...reg,
          presence: {
            ...currentPresence,
            notes
          }
        }
      }
      return reg
    }))
  }

  const savePresences = async () => {
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const presencesToSave = registrations
        .filter(reg => reg.presence)
        .map(reg => ({
          registration_id: reg.id,
          monday_present: reg.presence!.monday_present,
          tuesday_present: reg.presence!.tuesday_present,
          wednesday_present: reg.presence!.wednesday_present,
          thursday_present: reg.presence!.thursday_present,
          friday_present: reg.presence!.friday_present,
          notes: reg.presence!.notes || null,
          recorded_by_user_id: userProfile?.id
        }))

      const { error } = await supabase
        .from('presences')
        .upsert(presencesToSave, {
          onConflict: 'registration_id'
        })

      if (error) throw error

      setSuccessMessage('Présences sauvegardées avec succès!')
      setHasUnsavedChanges(false)
      setTimeout(() => setSuccessMessage(null), 3000)
      
      // Refresh data
      await fetchRegistrations()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' }
  ]

  const getTodayDayKey = (): string | null => {
    const dayOfWeek = new Date().getDay()
    const map: Record<number, string> = { 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday' }
    return map[dayOfWeek] || null
  }

  const visibleDays = selectedDay ? days.filter(d => d.key === selectedDay) : days

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour</span>
          </button>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour</span>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des présences</h1>
            <p className="text-gray-600">
              {session.stage?.title} - {session.center?.name}
            </p>
          </div>
        </div>
        
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Day filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 font-medium mr-1">Vue par jour:</span>
        <button
          onClick={() => {
            const todayKey = getTodayDayKey()
            if (todayKey) setSelectedDay(selectedDay === todayKey ? null : todayKey)
          }}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selectedDay === getTodayDayKey()
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Calendar className="h-3.5 w-3.5" />
          <span>Aujourd'hui</span>
        </button>
        {days.map(day => (
          <button
            key={day.key}
            onClick={() => setSelectedDay(selectedDay === day.key ? null : day.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedDay === day.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {day.label}
          </button>
        ))}
        <button
          onClick={() => setSelectedDay(null)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selectedDay === null
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Semaine
        </button>
      </div>

      {/* Session Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Période</p>
              <p className="font-medium">{session.periode} - {session.semaine}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Users className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Inscrits</p>
              <p className="font-medium">{registrations.length} enfants</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Dates</p>
              <p className="font-medium">
                {new Date(session.start_date).toLocaleDateString('fr-FR')} - {new Date(session.end_date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Presence Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enfant
                </th>
                {visibleDays.map(day => (
                  <th key={day.key} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {day.label}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {registrations.map((registration) => (
                <tr key={registration.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {registration.kid?.prenom} {registration.kid?.nom}
                      </div>
                      <div className="text-sm text-gray-500">
                        {registration.user?.prenom} {registration.user?.nom}
                      </div>
                    </div>
                  </td>
                  {visibleDays.map(day => {
                    const isPresent = registration.presence?.[`${day.key}_present` as keyof Presence] as boolean ?? true
                    return (
                      <td key={day.key} className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => updatePresence(registration.id, day.key, !isPresent)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                            isPresent
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}
                        >
                          {isPresent ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <XCircle className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    )
                  })}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={registration.presence?.notes || ''}
                        onChange={(e) => updateNotes(registration.id, e.target.value)}
                        placeholder="Ajouter une note..."
                        className="flex-1 text-sm border-0 border-b border-gray-200 focus:border-blue-500 focus:ring-0 bg-transparent"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {registrations.length === 0 && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune inscription</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun enfant inscrit pour cette session.
          </p>
        </div>
      )}

      {/* Floating save button */}
      {registrations.length > 0 && hasUnsavedChanges && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={savePresences}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3.5 bg-blue-600 text-white rounded-full shadow-2xl hover:bg-blue-700 disabled:opacity-50 transition-all duration-200 hover:scale-105 font-medium"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </button>
        </div>
      )}
    </div>
  )
}